// LEGAL-LANE (fix 3) — the retainer shape of the CCV client-list import.
// PURE: no React/DOM/network, so the recording harness runs the same code the
// browser runs.
//
// The firm's file is the app's own client-import sheet with the retainer
// columns filled in by hand. After the identity columns it carries, per round,
// a TRIPLET:
//
//     "R_1 due (Provision)" | "R_1 paid" | "Compta"
//
// Three properties of that header force POSITIONAL parsing:
//
//   1. "Compta" repeats verbatim for every round. XLSX's sheet_to_json
//      de-duplicates object keys ("Compta", "Compta_1", "Compta_2"), so the
//      TRUE header names survive only in the raw header row — read it with
//      { header: 1 } and index it, never trust the object keys for names.
//   2. One triplet is mislabelled: "R_3 due" appears where "R_3 paid" is
//      meant. Matching by name would read a paid amount as a due amount —
//      a money error, silent, in the direction of under-recording receipts.
//   3. The parenthetical carries the round's LABEL ("R_1 due (Provision)"),
//      except in the app's own downloaded template where it is the marker
//      "(info)" — which only means "this is not a label". The marker does
//      NOT suppress the due value (164 §A.2, RULINGS 2026-09-03): whether
//      due cells are applied is the importer's explicit checkbox choice,
//      never inferred from a header the exporter wrote.
//
// So: a round block STARTS at a due header; the column after it is that
// round's paid column positionally, even when misnamed; a following "Compta"
// column is that round's accounting reference. Anything the rules cannot
// account for is REPORTED, never guessed — see `warnings`.

const RE_DUE = /^\s*R[\s_]*(\d+)[\s_]*due\b/i;
const RE_PAID = /^\s*R[\s_]*(\d+)[\s_]*paid\b/i;
const RE_COMPTA = /^\s*compta\b/i;
const RE_LABEL = /\(([^)]*)\)/;

// The app's own template writes "(info)" as the parenthetical. It is a marker,
// not a name — a round must never be labelled "info".
const isMarkerLabel = (s) => /^\s*info\s*$/i.test(s || "");

const nameAt = (hdrs, i) => String((hdrs && hdrs[i]) != null ? hdrs[i] : "").trim();

/**
 * Parse the raw header row into round blocks.
 *
 * @param {string[]} rawHdrs the TRUE header names, in column order
 *                           (XLSX.utils.sheet_to_json(ws, { header: 1 })[0])
 * @returns {{rounds: Array, warnings: string[]}}
 *          rounds: [{ roundNumber, label, dueCol, paidCol, comptaCol }]
 *          — *Col are column INDEXES into the row, or null when absent.
 */
export function parseRetainerHeader(rawHdrs) {
  const h = Array.isArray(rawHdrs) ? rawHdrs : [];
  const rounds = [];
  const warnings = [];
  const seen = new Map();
  let i = 0;
  while (i < h.length) {
    const due = RE_DUE.exec(nameAt(h, i));
    if (!due) {
      i++;
      continue;
    }
    const roundNumber = +due[1];
    const labelMatch = RE_LABEL.exec(nameAt(h, i));
    // "(info)" is the app's OWN template marker. 164 §A.2 (RULINGS
    // 2026-09-03): the marker no longer decides anything about the VALUE —
    // whether a due cell is applied is the importer's explicit choice (the
    // apply-retainer checkbox), not something inferred from a header the
    // exporter wrote. The marker keeps the ONE thing it is still right
    // about: a round must never be labelled "info".
    const dueIsInfo = !!(labelMatch && isMarkerLabel(labelMatch[1]));
    const label =
      labelMatch && !dueIsInfo ? labelMatch[1].trim() : "";

    // --- the paid column: POSITIONAL, the column immediately after the due ---
    let paidCol = null;
    const next = nameAt(h, i + 1);
    const nextPaid = RE_PAID.exec(next);
    const nextDue = RE_DUE.exec(next);
    if (i + 1 < h.length) {
      if (nextPaid) {
        paidCol = i + 1;
      } else if (nextDue && +nextDue[1] === roundNumber) {
        // The stated mislabel: "R_3 due" sitting where "R_3 paid" belongs.
        // Same round number ⇒ it is this round's second money column, which
        // is paid. A DIFFERENT round number is a new round, not this one's
        // paid column — that distinction is what keeps a due-only sheet safe.
        paidCol = i + 1;
        warnings.push(
          `Column ${i + 2} is headed "${next}" but sits in round ${roundNumber}'s paid position; read as PAID.`,
        );
      } else if (!next) {
        paidCol = i + 1;
      } else if (!RE_COMPTA.test(next) && !nextDue) {
        // An unrecognised header in the paid slot: do not guess at money.
        warnings.push(
          `Round ${roundNumber}: column ${i + 2} ("${next}") is not a recognised paid column — no paid amount imported for this round.`,
        );
      }
    }

    // --- the compta column: the next column, if it is headed Compta ---------
    const comptaAt = paidCol !== null ? i + 2 : i + 1;
    let comptaCol = null;
    if (comptaAt < h.length && RE_COMPTA.test(nameAt(h, comptaAt)))
      comptaCol = comptaAt;

    if (seen.has(roundNumber)) {
      warnings.push(
        `Round ${roundNumber} appears more than once in the header; the later block (column ${i + 1}) is ignored.`,
      );
    } else {
      seen.set(roundNumber, true);
      // 164 §A.2 — dueCol is always the column index. `dueIsInfo` left the
      // shape in the same change: a census found no reader outside this
      // function.
      rounds.push({
        roundNumber,
        label,
        dueCol: i,
        paidCol,
        comptaCol,
      });
    }
    i = (comptaCol !== null ? comptaCol : paidCol !== null ? paidCol : i) + 1;
  }
  rounds.sort((a, b) => a.roundNumber - b.roundNumber);
  return { rounds, warnings };
}

/**
 * Money cell → number | null, with a REFUSAL rather than a guess.
 *
 * The existing importer's coercion is kept verbatim for the cases it already
 * handled (Quantum, R<n>_paid): strip spaces, comma→dot. What is added is a
 * refusal: a string carrying BOTH '.' and ',' is ambiguous between the
 * Belgian "1.234,56" and the anglo "1,234.56", and the old expression silently
 * read the first as 1.234. Amounts are money; an ambiguous cell is reported,
 * never resolved by preference.
 *
 * @returns {{ value: number|null, error: string|null }}
 */
export function parseMoneyCell(raw) {
  if (raw === "" || raw == null) return { value: null, error: null };
  if (typeof raw === "number")
    return isFinite(raw) ? { value: raw, error: null } : { value: null, error: "not a finite number" };
  const s = String(raw).trim();
  if (!s) return { value: null, error: null };
  const bare = s.replace(/[\s €]/g, "");
  if (bare.includes(".") && bare.includes(","))
    return {
      value: null,
      error: `ambiguous number "${s}" (both . and , present — cannot tell thousands from decimals)`,
    };
  const n = parseFloat(bare.replace(",", "."));
  if (isNaN(n)) return { value: null, error: `not a number: "${s}"` };
  return { value: n, error: null };
}

/**
 * Compta cell → a reference string.
 *
 * The column holds either a journal reference ("L-23-0038") or an accounting
 * DATE that Excel hands over as a serial day-number. A serial stored verbatim
 * ("45678") is meaningless to a reader, so a plausible serial is rendered as
 * YYYY-MM-DD; everything else is kept as written.
 *
 * @param {*} raw
 * @param {(v:any)=>string|null} parseDateCell injected from importCells.mjs so
 *        this module stays free of that dependency's date policy.
 */
export function parseComptaCell(raw, parseDateCell) {
  if (raw === "" || raw == null) return "";
  if (typeof raw === "number" && isFinite(raw)) {
    const iso = parseDateCell ? parseDateCell(raw) : null;
    return iso ? String(iso).slice(0, 10) : String(raw);
  }
  if (raw instanceof Date) {
    const iso = parseDateCell ? parseDateCell(raw) : null;
    return iso ? String(iso).slice(0, 10) : "";
  }
  return String(raw).trim();
}

/**
 * Reconcile the header's rounds against the rounds the collective case already
 * has. Existing rounds MATCH BY INDEX (roundNumber) and are never duplicated;
 * a round the header knows and the case does not is created, taking its label
 * from the parenthetical.
 *
 * The created round's formula is "0", deliberately — UNLESS the file carries
 * the app's own round definitions (164 §A.5(c): the export appends a "Rounds"
 * sheet with RoundNumber/Label/Formula/DueDate, handed here as the optional
 * `sheetDefs` map {roundNumber → {label, formula, dueDate}}). A round the
 * case does NOT have takes the sheet's definition; a round the case HAS is
 * untouched — the app's definition wins, always. Without a definition, "0"
 * remains the honest default: a retainer round's due is normally derived
 * (formula × the client's quantum), but a due-column file states a due PER
 * CLIENT, so there is no single formula to infer — and inventing one that
 * happened to fit the imported rows would produce confident, wrong numbers
 * for every client added later.
 *
 * @returns {{ merged: Array, created: Array }}
 */
export function reconcileRounds(existingRounds, headerRounds, labelFor, sheetDefs) {
  const existing = Array.isArray(existingRounds) ? existingRounds : [];
  const byNumber = new Map(existing.map((r) => [+r.roundNumber, r]));
  const merged = existing.slice();
  const created = [];
  for (const hr of headerRounds) {
    const found = byNumber.get(hr.roundNumber);
    if (found) continue;
    const def = sheetDefs ? sheetDefs[hr.roundNumber] : null;
    const round = {
      roundNumber: hr.roundNumber,
      label:
        (def && def.label) ||
        hr.label ||
        (labelFor ? labelFor(hr.roundNumber) : `Round ${hr.roundNumber}`),
      dueDate: (def && def.dueDate) || "",
      formula:
        def && String(def.formula ?? "").trim() !== ""
          ? String(def.formula).trim()
          : "0",
    };
    merged.push(round);
    created.push(round);
    byNumber.set(hr.roundNumber, round);
  }
  merged.sort((a, b) => +a.roundNumber - +b.roundNumber);
  return { merged, created };
}
